import { SHCheckbox } from "@components/design-systems";
import React, { useEffect, useMemo } from "react";
import { useMSTableColumnSettingVisibility } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/column-setting/utils";
import { ModelScreenerColumnSettingData } from "@models/model-screener/entities/modelScreenerFilter";
import { useToggle } from "react-use";
import { debounce } from "lodash";
import {
  MSColumnSettingsDefaultValueConstants
} from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/column-setting/constants";

type MSTableColumnSettingMainHeaderVisibilityCheckboxProps = {
  mainHeaderId: string;
  defaultChecked?: boolean;
  disabled?: boolean;
};

export const MSTableColumnSettingMainHeaderVisibilityCheckbox = React.memo(
  ({
    mainHeaderId,
    defaultChecked = false,
    disabled = false,
  }: MSTableColumnSettingMainHeaderVisibilityCheckboxProps) => {
    // Forms
    const {
      getValues,
      setValue,
      isAnySubHeaderVisible,
      currentMainHeaderFieldPath,
    } = useMSTableColumnSettingVisibility({
      mainHeaderId,
    });

    // States
    const [isChecked, toggleChecked] = useToggle(defaultChecked);

    // Functions
    const handleOnClick = () => {
      const newCheckValue = !isChecked;
      toggleChecked(newCheckValue);

      // Update main header visibility
      setValue(currentMainHeaderFieldPath.isVisible, newCheckValue);

      // Update all sub-headers visibility
      const currentSubHeaders =
        (getValues(
          currentMainHeaderFieldPath.columnSettings,
        ) as ModelScreenerColumnSettingData[]) || [];
      const updatedSubHeaders = currentSubHeaders.map((item) => ({
        ...item,
        isVisible: newCheckValue,
      }));

      setValue(currentMainHeaderFieldPath.columnSettings, updatedSubHeaders);
    };

    // Effects
    const debounceUpdateValueByFormData = useMemo(
      () =>
        debounce(
          (formDataIsAnySubHeaderVisible: boolean, currentChecked: boolean) => {
            if (!formDataIsAnySubHeaderVisible) {
              if (!currentChecked) return;
              toggleChecked(false);
              setValue(currentMainHeaderFieldPath.isVisible, false);
            } else {
              if (currentChecked) return;
              toggleChecked(true);
              setValue(currentMainHeaderFieldPath.isVisible, true);
            }
          },
          MSColumnSettingsDefaultValueConstants.StandardDelayTimeMs,
        ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    useEffect(() => {
      debounceUpdateValueByFormData(isAnySubHeaderVisible, isChecked);
      return () => debounceUpdateValueByFormData.cancel();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAnySubHeaderVisible]);

    return (
      <SHCheckbox
        disabled={disabled}
        checked={isChecked}
        onClick={handleOnClick}
        sx={{
          padding: 0.5,
        }}
      />
    );
  },
);
